import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  formatDateRu,
  formatTimeRu,
} from "../../../utils/appointmentLabels";
import clinicAPI from "../../../services/clinicApi";
import {
  PatientMembershipPanel,
  PatientAttendancePanel,
} from "./PatientSportPanel";
import { GENDER_OPTIONS } from "./patientConstants";
import {
  COMPETITION_STATUS_LABELS,
  competitionResultLabel,
  pollAnswerLabel,
  pollAnswerTone,
} from "./clientSportDisplay";
import s from "../clinic.module.css";
import {
  Button,
  Field,
  Input,
  PhoneInput,
  EmptyState,
} from "../../ui";
import L from "../../../constants/baysportLabels";

export function ClientInfoSection({
  patient,
  edit,
  setEdit,
  onSave,
  editing,
  onToggleEdit,
  embedded = false,
  inFold = false,
}) {
  const genderLabel =
    GENDER_OPTIONS.find((g) => g.value === edit.gender)?.label || "—";
  const tg = edit.telegram_username || patient?.patient_card?.telegram_username;
  const tgUrl = tg ? `https://t.me/${String(tg).replace(/^@/, "")}` : null;

  const content = (
    <>
      {!inFold ? (
        <div className={embedded ? s.clientProfileEmbeddedHead : s.clientProfileSectionHead}>
          {embedded ? (
            <h3 className={s.clientTabCardTitle}>Контакты</h3>
          ) : (
            <h2 className={s.clientProfileSectionTitle}>Основная информация</h2>
          )}
          <button type="button" className={s.clientProfileSectionAction} onClick={onToggleEdit}>
            {editing ? "Готово" : "Редактировать"}
          </button>
        </div>
      ) : null}

      {editing ? (
        <form
          className={s.clientTabFormStack}
          onSubmit={(e) => {
            e.preventDefault();
            onSave(e);
            onToggleEdit();
          }}
        >
          <Field label="ФИО">
            <Input
              value={edit.full_name}
              onChange={(e) => setEdit({ ...edit, full_name: e.target.value })}
            />
          </Field>
          <Field label="Телефон">
            <PhoneInput
              value={edit.phone || ""}
              onChange={(v) => setEdit({ ...edit, phone: v })}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={edit.email}
              onChange={(e) => setEdit({ ...edit, email: e.target.value })}
            />
          </Field>
          <Field label="Telegram">
            <Input
              value={edit.telegram_username}
              onChange={(e) =>
                setEdit({ ...edit, telegram_username: e.target.value })
              }
              placeholder="@username"
            />
          </Field>
          <Field label="Дата рождения">
            <Input
              type="date"
              value={edit.date_of_birth || ""}
              onChange={(e) =>
                setEdit({ ...edit, date_of_birth: e.target.value })
              }
            />
          </Field>
          <Field label="Пол">
            <select
              className={s.clientProfileSelect}
              value={edit.gender}
              onChange={(e) => setEdit({ ...edit, gender: e.target.value })}
            >
              {GENDER_OPTIONS.map((g) => (
                <option key={g.value || "empty"} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Адрес">
            <Input
              value={edit.address}
              onChange={(e) => setEdit({ ...edit, address: e.target.value })}
            />
          </Field>
          <Field label="Контакт для экстренных случаев">
            <Input
              value={edit.emergency_contact_name}
              onChange={(e) =>
                setEdit({ ...edit, emergency_contact_name: e.target.value })
              }
              placeholder="ФИО"
            />
          </Field>
          <Field label="Телефон экстренного контакта">
            <Input
              value={edit.emergency_contact_phone}
              onChange={(e) =>
                setEdit({ ...edit, emergency_contact_phone: e.target.value })
              }
            />
          </Field>
          <Button type="submit" size="sm">
            Сохранить
          </Button>
        </form>
      ) : (
        <dl className={s.clientDl}>
          <dt>Телефон</dt>
          <dd>
            {patient?.phone ? (
              <a href={`tel:${patient.phone}`}>{patient.phone}</a>
            ) : (
              "—"
            )}
          </dd>
          <dt>Email</dt>
          <dd>
            {patient?.email && !patient.email.includes("@patient.internal")
              ? patient.email
              : "—"}
          </dd>
          <dt>Telegram</dt>
          <dd>
            {tgUrl ? (
              <a href={tgUrl} target="_blank" rel="noreferrer">
                {tg.startsWith("@") ? tg : `@${tg}`}
              </a>
            ) : (
              "—"
            )}
          </dd>
          <dt>Дата рождения</dt>
          <dd>
            {patient?.date_of_birth
              ? formatDateRu(`${patient.date_of_birth}T12:00:00`, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "—"}
          </dd>
          <dt>Пол</dt>
          <dd>{genderLabel}</dd>
          <dt>Адрес</dt>
          <dd>{edit.address?.trim() || "—"}</dd>
          {(edit.emergency_contact_name?.trim() || edit.emergency_contact_phone?.trim()) ? (
            <>
              <dt>Экстренный контакт</dt>
              <dd>
                {[edit.emergency_contact_name, edit.emergency_contact_phone]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </dd>
            </>
          ) : null}
        </dl>
      )}
    </>
  );

  if (embedded) {
    return <div className={s.clientProfileEmbeddedBlock}>{content}</div>;
  }

  return (
    <section id="client-info" className={s.clientProfileSection}>
      {content}
    </section>
  );
}

/** @deprecated use ClientInfoSection in single-page profile */
export function ClientMainSection(props) {
  return <ClientInfoSection {...props} editing={false} onToggleEdit={() => {}} />;
}

export function ClientSportSection({ profile, edit, setEdit, onSave, embedded = false }) {
  const groups = profile?.groups || [];
  const rank = edit?.rank_belt || profile?.client?.patient_card?.rank_belt;

  const body = (
    <>
      <div className={s.clientTabCard}>
        <h3 className={s.clientTabCardTitle}>Группы</h3>
        {groups.length === 0 ? (
          <EmptyState title="Не зачислен в группы" />
        ) : (
          <ul className={s.clientGroupList}>
            {groups.map((g) => (
              <li key={g.id} className={s.clientGroupListItem}>
                <Link to={`/manage/app/groups?group=${g.id}`} className={s.clientGroupLink}>
                  <strong>{g.name}</strong>
                </Link>
                {g.sport_direction ? (
                  <span className={s.clientTabMuted}>{g.sport_direction}</span>
                ) : null}
                {g.trainer_name ? (
                  <span className={s.clientTabMuted}>Тренер: {g.trainer_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={s.clientTabCard}>
        <div className={s.clientProfileSectionHead}>
          <h3 className={s.clientTabCardTitle}>Разряд / пояс / уровень</h3>
        </div>
        <form
          className={s.clientTabFormRow}
          onSubmit={(e) => {
            e.preventDefault();
            onSave?.(e);
          }}
        >
          <Field label="Звание" className={s.clientTabFieldGrow}>
            <Input
              value={edit?.rank_belt || ""}
              onChange={(e) => setEdit?.({ ...edit, rank_belt: e.target.value })}
              placeholder="Например: 1 разряд, синий пояс…"
            />
          </Field>
          <div className={s.clientTabFormBtn}>
            <Button type="submit" size="sm" disabled={!onSave}>
              Сохранить
            </Button>
          </div>
        </form>
        {!edit?.rank_belt && rank ? (
          <p className={s.clientTabMuted}>Текущее: {rank}</p>
        ) : null}
      </div>
    </>
  );

  if (embedded) {
    return <div className={s.clientProfileTabPanel}>{body}</div>;
  }

  return (
    <section id="client-sport" className={s.clientProfileSection}>
      <div className={s.clientProfileSectionHead}>
        <h2 className={s.clientProfileSectionTitle}>Спорт</h2>
      </div>
      {body}
    </section>
  );
}

export function ClientMembershipsSection({ patientId, profile, onChanged, compact = false }) {
  return (
    <PatientMembershipPanel
      patientId={patientId}
      payments={profile?.payments || []}
      onChanged={onChanged}
      compact={compact}
    />
  );
}

export function ClientAttendanceSection({ patientId }) {
  return <PatientAttendancePanel patientId={patientId} />;
}

export function ClientEventsSection({ patientId, profile, compact = false }) {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const competitions = profile?.competitions || [];

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    clinicAPI
      .getPatientPollHistory(patientId)
      .then((r) => setPolls(r.responses || []))
      .catch(() => setPolls([]))
      .finally(() => setLoading(false));
  }, [patientId]);

  const panel = (
    <div className={compact ? s.clientEventsPanel : s.clientProfileTabPanel}>
      <section className={s.clientEventsBlock}>
        <h3 className={s.clientEventsBlockTitle}>Соревнования</h3>
        {!competitions.length ? (
          <p className={s.clientTabMuted}>
            Заявок на соревнования пока нет.
          </p>
        ) : (
          <ul className={s.clientEventsList}>
            {competitions.map((reg) => {
              const result = competitionResultLabel(reg);
              return (
                <li key={reg.id} className={s.clientEventsRow}>
                  <div className={s.clientEventsRowMain}>
                    <strong>{reg.session?.title || "Соревнование"}</strong>
                    <span className={s.clientEventsRowMeta}>
                      {reg.session?.start_datetime
                        ? formatDateRu(reg.session.start_datetime, {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        : "Дата не указана"}
                      {result ? ` · ${result}` : ""}
                    </span>
                  </div>
                  <span className={s.clientEventsBadge}>
                    {COMPETITION_STATUS_LABELS[reg.status] || reg.status || "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className={s.clientEventsBlock}>
        <h3 className={s.clientEventsBlockTitle}>Опросы о явке</h3>
        {loading ? (
          <p className={s.clientTabMuted}>Загрузка…</p>
        ) : !polls.length ? (
          <p className={s.clientTabMuted}>Ответов на опросы пока нет.</p>
        ) : (
          <ul className={s.clientEventsList}>
            {polls.map((row) => {
              const tone = pollAnswerTone(row);
              const sessionTitle =
                row.session?.title ||
                row.poll?.title ||
                row.session?.group?.name ||
                "Занятие";
              return (
                <li key={row.id} className={s.clientEventsRow}>
                  <div className={s.clientEventsRowMain}>
                    <strong>{sessionTitle}</strong>
                    <span className={s.clientEventsRowMeta}>
                      {row.session?.group?.name &&
                      row.session?.group?.name !== sessionTitle
                        ? `${row.session.group.name} · `
                        : ""}
                      {row.responded_at
                        ? `${formatDateRu(row.responded_at)} ${formatTimeRu(row.responded_at)}`
                        : "Без даты"}
                    </span>
                  </div>
                  <span
                    className={`${s.clientEventsBadge} ${
                      tone === "ok"
                        ? s.clientEventsBadge_ok
                        : tone === "warn"
                          ? s.clientEventsBadge_warn
                          : tone === "muted"
                            ? s.clientEventsBadge_muted
                            : ""
                    }`}
                  >
                    {pollAnswerLabel(row)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );

  if (compact) {
    return <div id="client-events">{panel}</div>;
  }

  return (
    <section id="client-events" className={s.clientProfileSection}>
      <div className={s.clientProfileSectionHead}>
        <h2 className={s.clientProfileSectionTitle}>События и опросы</h2>
      </div>
      {panel}
    </section>
  );
}

export function ClientDocumentsSection({ profile }) {
  const docs = profile?.documents || [];
  return (
    <div className={s.clientProfileTabPanel}>
      <section className={s.clientTabCard}>
        <h3 className={s.clientTabCardTitle}>Документы</h3>
        {!docs.length ? (
          <EmptyState
            title="Документов пока нет"
            description="Договоры и согласия появятся после подключения модуля"
          />
        ) : (
          <ul className={s.clientSimpleList}>
            {docs.map((d) => (
              <li key={d.id}>
                {d.title} · {d.created_at}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
